import { SentryFeatureTags, logException } from "@punchup/sentry";
import { SpecialRow, getAllPublishedSpecialsForComedian } from "./specials";

import { Comedian } from "./comedian";
import { Database } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import dayjs from "dayjs";
import { getCachedVideoRanks } from "./video";

export type VideoComedianContext = {
  comedian: Comedian;
  recommendedVideos: SpecialRow[] | null;
  isPreview?: boolean;
  isLoggedIn?: boolean;
  isFollowing?: boolean;
  nextComedianData?: {
    slug: string;
    id: string;
  };
};

type SpecialScored = SpecialRow & {
  score: number;
};

/**
 * This returns 3 recommended specials based on the currentlyPlayingSpecial.
 * Right now, it's fairly simple and just returns the next 3 specials in the list.
 * Additional ideas for future improvement:
 * - Reduce ranking for videos watched recently
 * - Increate ranking for videos posted recently
 * - Increase ranking for videos with high views
 * - Introduce videos from other comedians
 */
export async function getRecommendedVideos(
  currentlyPlayingSpecial: SpecialRow
): Promise<SpecialScored[] | null> {
  const specials = (
    await getAllPublishedSpecialsForComedian(
      currentlyPlayingSpecial.comedian_id
    )
  ).map((special) => ({ ...special, score: 0 }));

  // Find the current special in the list
  const currentIndex = specials.findIndex(
    (special) => special.id === currentlyPlayingSpecial.id
  );

  // Only set score to -1 if the special was found in the array
  if (currentIndex !== -1) {
    specials[currentIndex].score = -1; // Don't recommend the same special
  }

  // Rank based on returned order (currently alphabetical by title)
  for (let i = 1; i < specials.length; i++) {
    const idx = (i + currentIndex) % specials.length;
    specials[idx].score = i;

    // Note: This will not recommend specials that require a subscription or are paid even if the user has a subscription or has paid
    if (
      specials[idx].requires_subscription ||
      specials[idx].price > 0 ||
      dayjs().isBefore(specials[idx].published_at)
    ) {
      specials[idx].score = -1;
    }
  }

  // return the first 3 specials with the highest score, as long as score is >= 0
  return specials
    .filter((special) => special.score >= 0)
    .sort((a, b) => a.score - b.score)
    .slice(0, 3);
}

/**
 * Get recommended videos with different behavior based on navigation source:
 * - For direct navigation: Prioritize same-comedian content first
 * - For queue navigation: Just follow the queue ordering
 */
export async function getEnhancedRecommendedVideos(
  supabase: SupabaseClient<Database>,
  currentlyPlayingSpecial: SpecialRow,
  includeOtherComedians: boolean = false,
  maxResults: number = 5,
  watchedSpecialIds: string[] = [],
  fromQueue: boolean = false
): Promise<SpecialScored[]> {
  if (!currentlyPlayingSpecial || !currentlyPlayingSpecial.id) {
    return []; // Return empty array for invalid input
  }

  // STEP 1: Check for more specials from the same comedian, but ONLY if not from queue
  if (!fromQueue) {
    const { data: sameComedianSpecials, error: comedianError } = await supabase
      .from("specials")
      .select("*")
      .eq("comedian_id", currentlyPlayingSpecial.comedian_id)
      .neq("id", currentlyPlayingSpecial.id)
      .not(
        "id",
        "in",
        watchedSpecialIds.length > 0
          ? `(${watchedSpecialIds.join(",")})`
          : "(0)"
      );

    if (comedianError) {
      logException(comedianError, {
        extra: {
          data: "Error fetching same-comedian specials from Supabase",
          comedianId: currentlyPlayingSpecial.comedian_id,
        },
        tags: {
          feature: SentryFeatureTags.Recommendations,
          supabase: true,
        },
      });
    }

    // If manually navigated and there are still unwatched specials from the same comedian, return only those
    if (
      !comedianError &&
      sameComedianSpecials &&
      sameComedianSpecials.length > 0
    ) {
      const scoredSpecials = await formatSpecials(
        sameComedianSpecials.map((s) => ({ ...s, score: 1 })),
        supabase,
        maxResults,
        watchedSpecialIds
      );

      return scoredSpecials;
    }
  }

  // We're either navigated from queue OR we've exhausted all specials from the current comedian
  if (!includeOtherComedians) {
    return [];
  }

  // STEP 2: Get followed comedians and video rankings
  let followedComedianIds: string[] = [];
  let videoRanks: Record<string, number> = {};

  const { data: sessionData } = await supabase.auth.getSession();
  const isLoggedIn = !!sessionData.session?.user;

  // Get followed comedians if logged in
  if (isLoggedIn) {
    const { data: follows } = await supabase
      .from("user_comedian_follows")
      .select("comedian_id")
      .eq("user_id", sessionData.session!.user.id);

    followedComedianIds = follows?.map((f) => f.comedian_id) || [];
  }

  try {
    const rankData = await getCachedVideoRanks();
    videoRanks = rankData.reduce(
      (acc, item) => {
        acc[item.video_id] = item.score ?? 0;
        return acc;
      },
      {} as Record<string, number>
    );
  } catch (error) {
    logException(error, {
      extra: {
        data: "Error loading cached video ranks or reducing them",
      },
      tags: {
        feature: SentryFeatureTags.Recommendations,
      },
    });
  }

  // STEP 3: Get followed comedians' specials first
  const now = dayjs().toISOString();
  let followedSpecials: any[] = [];
  if (followedComedianIds.length > 0) {
    const { data: followedSpecialsData } = await supabase
      .from("specials")
      .select("*")
      .in("comedian_id", followedComedianIds)
      .neq("id", currentlyPlayingSpecial.id)
      .neq("comedian_id", currentlyPlayingSpecial.comedian_id)
      .neq("requires_subscription", true)
      .neq("is_live", false)
      .lte("published_at", now); // removes test specials with null published_at

    followedSpecials = followedSpecialsData || [];
  }

  // STEP 4: Get other specials (prioritize recent ones)
  const threeMonthsAgo = dayjs().subtract(3, "month").toISOString();
  const { data: otherSpecials } = await supabase
    .from("specials")
    .select("*")
    .neq("id", currentlyPlayingSpecial.id)
    .neq("comedian_id", currentlyPlayingSpecial.comedian_id)
    .not(
      "comedian_id",
      "in",
      followedComedianIds.length > 0
        ? `(${followedComedianIds.join(",")})`
        : "(0)"
    )
    .gte("published_at", threeMonthsAgo)
    .lte("published_at", now) // removes test specials with null published_at
    .order("published_at", { ascending: false })
    .neq("requires_subscription", true)
    .neq("is_live", false)
    .limit(maxResults * 2);

  // STEP 5: Combine and sort by ranking
  const combinedSpecials = [
    // CRUCIAL: look up by the special's UUID (s.id), not by s.video_id
    ...followedSpecials.map((s) => ({
      ...s,
      score: videoRanks[s.id] ?? 0,
      isFollowed: true,
    })),
    ...(otherSpecials || []).map((s) => ({
      ...s,
      score: videoRanks[s.id] ?? 0,
      isFollowed: false,
    })),
  ];

  // Sort by ranking score (higher is better) and then followed status
  combinedSpecials.sort((a, b) => {
    // First priority: followed status
    if (a.isFollowed && !b.isFollowed) return -1;
    if (!a.isFollowed && b.isFollowed) return 1;

    return b.score - a.score;
  });

  const unwatchedSpecials = combinedSpecials.filter(
    (special) => !watchedSpecialIds.includes(special.id)
  );

  const scoredSpecials = await formatSpecials(
    unwatchedSpecials.map((s) => ({
      ...s,
      score: s.score,
    })),
    supabase,
    maxResults,
    watchedSpecialIds
  );

  // Double-check no watched videos made it through
  const verifiedUnwatched = scoredSpecials.filter(
    (s) => !watchedSpecialIds.includes(s.id)
  );

  return verifiedUnwatched;
}

/**
 * Helper function to format specials with comedian info
 */
async function formatSpecials(
  specials: SpecialScored[],
  supabase: SupabaseClient<Database>,
  maxResults: number,
  watchedSpecialIds: string[] = []
): Promise<SpecialScored[]> {
  // Format the specials for the queue
  const scoredSpecials = specials.map((special) => {
    return {
      ...special,
      comedian_slug: "",
      comedian_name: "",
    };
  });

  // Get comedian information for display
  if (scoredSpecials.length > 0) {
    const comedianIds = Array.from(
      new Set(scoredSpecials.map((s) => s.comedian_id))
    );
    const { data: comedians } = await supabase
      .from("comedians")
      .select("id, slug, display_name")
      .in("id", comedianIds);

    // Add comedian info to specials
    if (comedians) {
      const comedianMap = new Map<string, { slug: string; name: string }>(
        comedians.map(
          (c: { id: string; slug: string; display_name: string }) => [
            c.id,
            { slug: c.slug, name: c.display_name },
          ]
        )
      );

      scoredSpecials.forEach((special) => {
        const comedianInfo = comedianMap.get(special.comedian_id);
        if (comedianInfo) {
          special.comedian_slug = comedianInfo.slug;
          special.comedian_name = comedianInfo.name;
        }
      });
    }
  }
  return scoredSpecials.slice(0, maxResults);
}
